import { bool, func, number, object, string } from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

import { isEmpty } from 'ravenjs/utils/lodash';

import { DEFAULT_COLORS } from 'constants/colors';
import AppSubNavigationMain from 'components/AppSubNavigationMain';
import AppSubNavigationTopics from 'components/AppSubNavigationTopics';
import { isNavigationV2Enabled as isNavV2Enabled } from 'utils/company';
import { HOVER_BUFFER_ZONE, SUBMENU_TOP_OFFSET } from 'constants/navigation';

const AppSubNavigationStyledCommon = styled.div`
    display: flex;
    position: absolute;
    background: ${DEFAULT_COLORS.WHITE};
    color: ${DEFAULT_COLORS.BLACK};
    z-index: 100;
    right: ${({ subNavOverflow }) => (subNavOverflow ? 0 : 'auto')};
`;

const AppSubNavigationStyledV1 = styled(AppSubNavigationStyledCommon)`
    top: 42px;
    border: 1px solid ${DEFAULT_COLORS.BLACK};
    padding: 2.094rem 2.094rem 2.094rem 2.094rem;
    margin: 0 0.625rem 0 0;
`;

const AppSubNavigationStyledV2 = styled(AppSubNavigationStyledCommon)`
    top: 52px;
    border: 1px solid ${DEFAULT_COLORS.GREY_22};
    border-radius: 4px 4px 8px 8px;
    box-shadow: 0 2px 8px -2px rgba(40, 40, 40, 0.16);
    padding: 24px;
    width: fit-content;
    /*  Adjust max-width to account for focus outline space (4px per side: 2px outline + 2px offset on each side) */
    max-width: calc(100vw - 164px - 8px);
    overflow: visible; /* Ensure focus outlines are not clipped */
    opacity: ${({ $isPositioned }) => ($isPositioned ? 1 : 0)};
    transition: opacity 0.15s ease-in-out;

    &::before {
        content: '';
        position: absolute;
        top: -${HOVER_BUFFER_ZONE + SUBMENU_TOP_OFFSET}px;
        left: 0;
        right: 0;
        height: ${HOVER_BUFFER_ZONE + SUBMENU_TOP_OFFSET}px;
    }

    /* Extended hover zone for cursor forgiveness (pointer interaction tolerance) */
    &::after {
        content: '';
        position: absolute;
        top: -${HOVER_BUFFER_ZONE}px;
        left: -${HOVER_BUFFER_ZONE}px;
        right: -${HOVER_BUFFER_ZONE}px;
        bottom: -${HOVER_BUFFER_ZONE}px;
        z-index: -1;
    }
`;

const ArrowStyled = styled.div`
    width: 16px;
    height: 16px;
    border-top: 1px solid ${DEFAULT_COLORS.BLACK};
    border-left: 1px solid ${DEFAULT_COLORS.BLACK};
    position: absolute;
    top: 34px;
    right: 40%;
    transform: rotate(45deg);
    background: ${DEFAULT_COLORS.WHITE};
    z-index: 1000;
    box-shadow: none;
`;

const AppSubNavigation = props => {
    const {
        isImpersonatingUser,
        show,
        nav,
        scrollTop,
        subnav,
        state,
        isVisibleOnDOM,
        onMouseEnter,
        onMouseLeave,
        parentNavItemId,
        onSubmenuItemClick,
    } = props;

    const [subNavOverflow, setSubNavOverflow] = useState(false);
    const [contentMaxHeight, setContentMaxHeight] = useState(undefined);
    const [isPositioned, setIsPositioned] = useState(false);

    const containerRef = useRef(null);

    const isNavigationV2Enabled = isNavV2Enabled();

    // Check for overflow using ref-based calculation instead of VisibilitySensor
    const checkOverflow = useCallback(() => {
        if (!containerRef.current || !isNavigationV2Enabled) return;

        const rect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const headerPadding = 82; // 5.125rem = 82px
        const focusOutlineBuffer = 8; // 2px outline + 2px offset on each side = 4px per side

        // Check if the submenu extends beyond the viewport's right edge
        const overflows = rect.right + focusOutlineBuffer > viewportWidth - headerPadding;
        setSubNavOverflow(overflows);
    }, [isNavigationV2Enabled]);

    // Reset overflow and height state when menu changes
    useEffect(() => {
        if (isNavigationV2Enabled) {
            setSubNavOverflow(false);
            setContentMaxHeight(undefined);
            setIsPositioned(false);
        }
    }, [state, show, isNavigationV2Enabled]);

    // Check horizontal overflow and compute available vertical space after render
    useLayoutEffect(() => {
        if (show && isNavigationV2Enabled) {
            // Small delay to ensure the submenu is fully rendered before measuring
            let timeoutId;
            const rafId = requestAnimationFrame(() => {
                timeoutId = setTimeout(() => {
                    checkOverflow();
                    if (containerRef.current) {
                        const rect = containerRef.current.getBoundingClientRect();
                        const viewportHeight = window.innerHeight;
                        const bottomMargin = 16;
                        const containerVerticalPadding = 48; // 24px top + 24px bottom
                        const availableHeight =
                            viewportHeight - rect.top - containerVerticalPadding - bottomMargin;
                        setContentMaxHeight(availableHeight > 0 ? availableHeight : undefined);
                    }
                    // Mark as positioned after calculations are complete
                    setIsPositioned(true);
                }, 0);
            });
            return () => {
                cancelAnimationFrame(rafId);
                if (timeoutId) clearTimeout(timeoutId);
            };
        }
        setContentMaxHeight(undefined);
        setIsPositioned(false);
        return undefined;
    }, [show, isNavigationV2Enabled, checkOverflow]);

    const hasSubNav = () => {
        return (subnav.main && subnav.main.length) || (subnav.topics && subnav.topics.length);
    };

    const onClick = e => {
        // Need to stop propagation of the event as this component is a child of <NavItem>
        // For V2, allow keyboard events to bubble up for navigation support
        if (e.type === 'keydown' && isNavigationV2Enabled) {
            // V2: Let keyboard events bubble up for Escape/Arrow key handling
            return;
        }
        e.stopPropagation();
    };

    // Handler for mouse enter on submenu (V2 only - cancels close timeout)
    const handleMouseEnter = () => {
        if (isNavigationV2Enabled && onMouseEnter) {
            onMouseEnter();
        }
    };

    // Handler for mouse leave from submenu (V2 only - triggers delayed close)
    const handleMouseLeave = () => {
        if (isNavigationV2Enabled && onMouseLeave) {
            onMouseLeave();
        }
    };

    const renderTopicsOrContentSection = () => {
        if (!isEmpty(subnav.topics)) {
            if (isNavigationV2Enabled) {
                return (
                    <AppSubNavigationTopics
                        nav={nav}
                        items={subnav.topics}
                        isNavigationV2Enabled={isNavigationV2Enabled}
                        onSubmenuItemClick={onSubmenuItemClick}
                    />
                );
            }

            return (
                <div style={{ marginLeft: '4rem' }}>
                    <AppSubNavigationTopics
                        nav={nav}
                        items={subnav.topics}
                        onSubmenuItemClick={onSubmenuItemClick}
                    />
                </div>
            );
        }
        return null;
    };

    const Container = isNavigationV2Enabled ? AppSubNavigationStyledV2 : AppSubNavigationStyledV1;

    if (show && isVisibleOnDOM && hasSubNav()) {
        return (
            <>
                {!isNavigationV2Enabled && <ArrowStyled />}
                <Container
                    ref={containerRef}
                    isImpersonatingUser={isImpersonatingUser}
                    subNavOverflow={subNavOverflow}
                    scrollTop={scrollTop}
                    // V2: Pass positioning state for opacity control
                    {...(isNavigationV2Enabled ? { $isPositioned: isPositioned } : {})}
                    // V2 hover handlers for persistent hover behavior
                    onMouseEnter={isNavigationV2Enabled ? handleMouseEnter : undefined}
                    onMouseLeave={isNavigationV2Enabled ? handleMouseLeave : undefined}
                    onClick={isNavigationV2Enabled ? onClick : undefined}
                    role={isNavigationV2Enabled ? 'menu' : undefined}
                    aria-labelledby={
                        isNavigationV2Enabled && parentNavItemId ? parentNavItemId : undefined
                    }
                >
                    <div
                        onClick={isNavigationV2Enabled ? undefined : onClick}
                        onKeyDown={isNavigationV2Enabled ? undefined : onClick}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: isNavigationV2Enabled ? '24px' : undefined,
                            alignItems: isNavigationV2Enabled ? 'flex-start' : undefined,
                            ...(isNavigationV2Enabled && contentMaxHeight
                                ? {
                                      maxHeight: `${contentMaxHeight}px`,
                                      overflowY: 'auto',
                                      // Add padding to prevent focus outline clipping
                                      padding: '4px',
                                      margin: '-4px', // Compensate padding to maintain layout
                                  }
                                : {}),
                        }}
                    >
                        <AppSubNavigationMain
                            items={subnav.main}
                            mainState={state}
                            isNavigationV2Enabled={isNavigationV2Enabled}
                            onSubmenuItemClick={onSubmenuItemClick}
                        />
                        {renderTopicsOrContentSection()}
                    </div>
                </Container>
            </>
        );
    }

    return null;
};

AppSubNavigation.propTypes = {
    isImpersonatingUser: bool,
    scrollTop: number,
    show: bool,
    state: string,
    subnav: object,
    nav: object,
    isVisibleOnDOM: bool,
    onMouseEnter: func,
    onMouseLeave: func,
    parentNavItemId: string,
    keyboardOpened: bool,
    onSubmenuItemClick: func,
};

AppSubNavigation.defaultProps = {
    isImpersonatingUser: false,
    scrollTop: 0,
    show: false,
    state: '',
    subnav: {
        main: [],
        topics: [],
    },
    nav: {},
    isVisibleOnDOM: true,
    onMouseEnter: undefined,
    onMouseLeave: undefined,
    parentNavItemId: undefined,
    keyboardOpened: false,
    onSubmenuItemClick: undefined,
};

export { AppSubNavigation as AppSubNavigationUnwrapped };
export default withRouter(AppSubNavigation);
